import React, { useMemo, useRef, useEffect } from "react";

interface Mote {
  id: number;
  left: number;
  size: number;
  delay: number;
  duration: number;
  drift: number;
  opacity: number;
}

interface Shaft {
  id: number;
  left: number;
  width: number;
  opacity: number;
}

interface ColumnsBackgroundProps {
  className?: string;
  moteCount?: number;
  shaftCount?: number;
  color?: string;
}

/**
 * Cthulhu Invictus hero effect: warm sunset light shafts angling between
 * unseen marble columns, with slow golden dust motes drifting upward —
 * the air inside a Roman forum at dusk.
 */
const ColumnsBackground: React.FC<ColumnsBackgroundProps> = ({
  className = "",
  moteCount = 30,
  shaftCount = 5,
  color = "rgba(230, 195, 120, 0.55)",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      targetRef.current = { x: nx, y: ny };
    };
    const handleLeave = () => {
      targetRef.current = { x: 0, y: 0 };
    };

    window.addEventListener("mousemove", handleMove);
    container.addEventListener("mouseleave", handleLeave);

    let raf = 0;
    const tick = () => {
      const cur = currentRef.current;
      const tgt = targetRef.current;
      cur.x += (tgt.x - cur.x) * 0.05;
      cur.y += (tgt.y - cur.y) * 0.05;
      if (layerRef.current) {
        layerRef.current.style.transform = `translate3d(${cur.x * 22}px, ${cur.y * 10}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      container.removeEventListener("mouseleave", handleLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  const shafts = useMemo<Shaft[]>(
    () =>
      Array.from({ length: shaftCount }, (_, i) => {
        // Distribute shafts roughly evenly across the banner with jitter.
        const slot = (i + 0.5) / shaftCount;
        const jitter = (Math.random() - 0.5) * (1 / shaftCount) * 0.6;
        return {
          id: i,
          left: Math.max(0, Math.min(1, slot + jitter)) * 100,
          width: 90 + Math.random() * 110,
          opacity: 0.18 + Math.random() * 0.18,
        };
      }),
    [shaftCount]
  );

  const motes = useMemo<Mote[]>(
    () =>
      Array.from({ length: moteCount }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        size: 2 + Math.random() * 4,
        delay: -(Math.random() * 16),
        duration: 12 + Math.random() * 14,
        drift: -40 + Math.random() * 80,
        opacity: 0.4 + Math.random() * 0.5,
      })),
    [moteCount]
  );

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden relative ${className}`}
      style={{
        WebkitMaskImage:
          "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, black 0%, black 75%, transparent 100%)",
      }}
    >
      {/* Warm sunset wash across the upper banner */}
      <div
        className="absolute inset-x-0 top-0 h-2/3 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(220, 150, 70, 0.18) 0%, rgba(160, 90, 40, 0.08) 40%, transparent 75%)",
          mixBlendMode: "screen",
        }}
      />

      <div
        ref={layerRef}
        className="absolute inset-0 pointer-events-none"
        style={{ transition: "transform 0.1s linear", willChange: "transform" }}
      >
        {/* Angled light shafts — column-gap god rays */}
        {shafts.map((s) => (
          <div
            key={`shaft-${s.id}`}
            className="absolute top-0 bottom-0"
            style={{
              left: `${s.left}%`,
              width: s.width,
              transform: "translateX(-50%) skewX(-10deg)",
              background: `linear-gradient(to bottom, ${color} 0%, rgba(230,195,120,0.12) 55%, transparent 100%)`,
              opacity: s.opacity,
              filter: "blur(14px)",
              mixBlendMode: "screen",
              animation: `column-pulse ${10 + (s.id % 3) * 3}s ease-in-out ${-(s.id * 1.7)}s infinite`,
            }}
          />
        ))}

        {/* Slow golden motes drifting upward */}
        {motes.map((m) => (
          <span
            key={`mote-${m.id}`}
            className="absolute rounded-full"
            style={{
              left: `${m.left}%`,
              bottom: `-${m.size * 2}px`,
              width: m.size,
              height: m.size,
              background: `radial-gradient(circle at 50% 50%, rgba(245, 220, 160, 0.95) 0%, rgba(220, 175, 90, 0.4) 60%, transparent 100%)`,
              boxShadow: "0 0 6px rgba(230, 190, 110, 0.55)",
              opacity: 0,
              animation: `mote-rise ${m.duration}s ease-in-out ${m.delay}s infinite`,
              ["--mote-opacity" as string]: `${m.opacity}`,
              ["--mote-drift" as string]: `${m.drift}px`,
              willChange: "transform, opacity",
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes mote-rise {
          0% {
            transform: translate(0, 0) scale(0.6);
            opacity: 0;
          }
          15% {
            opacity: var(--mote-opacity, 0.6);
          }
          60% {
            transform: translate(calc(var(--mote-drift) * 0.6), -260px) scale(1);
          }
          85% {
            opacity: calc(var(--mote-opacity, 0.6) * 0.5);
          }
          100% {
            transform: translate(var(--mote-drift), -460px) scale(1.2);
            opacity: 0;
          }
        }
        @keyframes column-pulse {
          0%, 100% { opacity: 0.18; }
          50% { opacity: 0.42; }
        }
      `}</style>
    </div>
  );
};

export default ColumnsBackground;